import React from 'react';

const defaultColumns = ['Cookie name', 'Domain/Type', 'Expiration', 'Provider', 'Purpose'];

const cookieTables = [
  {
    id: 'necessary',
    title: 'Necessary Cookies',
    description:
      'These cookies are essential for the website to operate correctly. They enable core features such as remembering items saved in an online basket, storing your cookie preferences, saving your language settings, and logging into secure areas of our website.',
    rows: [
      {
        name: 'cookiefirst-consent',
        domain: 'your-domain-here',
        expiration: '1 year',
        type: 'Cookie',
        provider: 'CookieFirst',
        description: 'Saves your cookie preferences for this site. You can update or withdraw your consent at any time.',
      },
      {
        name: 'cookiefirst-consent',
        domain: 'your-domain-here',
        expiration: 'Persistent',
        type: 'Local Storage',
        provider: 'CookieFirst',
        description: 'Stores your cookie preference settings for future visits.',
      },
      {
        name: 'cookiefirst-id',
        domain: 'your-domain-here',
        expiration: 'Persistent',
        type: 'Local Storage',
        provider: 'CookieFirst',
        description: 'Contains a unique visitor ID so CookieFirst can identify returning users.',
      },
    ],
  },
  {
    id: 'performance',
    title: 'Performance Cookies',
    description:
      'Performance (analytics) cookies collect statistical information about how visitors use our site. We use this data to review performance and enhance the website\'s functionality.',
    rows: [],
  },
  {
    id: 'functional',
    title: 'Functional Cookies',
    description:
      'These cookies provide additional features that improve your experience. They may be placed by our website or by third-party service providers. Depending on your choices, they may enable live chat support, video playback, social media sharing, and sign-in via third-party accounts.',
    rows: [],
  },
  {
    id: 'advertising',
    title: 'Advertising / Tracking Cookies',
    description:
      'These cookies are set by external advertising partners. They track browsing behaviour across different websites to build a profile of your interests. If allowed, these cookies help us display targeted advertisements on other websites, measure the performance of advertising campaigns, and understand how many people viewed or interacted with our adverts.',
    rows: [],
  },
];

const consentLinks = [
  { text: 'Change your consent', action: 'change' },
  { text: 'Withdraw your consent', action: 'withdraw' },
];

const CookieTable = ({ rows }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-[#fdf5ed] p-6 rounded-lg border border-[#f4e7da] text-center text-[#666]">
        <p>No cookies in this category are currently being used on this website.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-[#eddac9] shadow-lg">
      <table className="w-full">
        <thead className="bg-[#f6e9dd] text-[0.65rem] md:text-xs uppercase tracking-[0.3em] text-[#2f4c74]">
          <tr>
            {defaultColumns.map((column) => (
              <th key={column} className="px-3 md:px-4 py-3 text-left">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-xs md:text-sm">
          {rows.map((row, index) => (
            <tr key={`${row.name}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-[#fdf5ed]'}>
              <td className="px-3 md:px-4 py-4 font-semibold text-[#1f1f1f] align-top">{row.name}</td>
              <td className="px-3 md:px-4 py-4 text-[#444] align-top">
                {row.domain && <div>{row.domain}</div>}
                {row.type && <div className="text-xs text-[#666]">{row.type}</div>}
              </td>
              <td className="px-3 md:px-4 py-4 text-[#444] align-top">{row.expiration}</td>
              <td className="px-3 md:px-4 py-4 text-[#444] align-top">{row.provider}</td>
              <td className="px-3 md:px-4 py-4 text-[#333] align-top">
                {row.description}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CookieNoticeSection = () => (
  <section id="cookie-notice" className="relative z-10 -mt-16 md:-mt-24 pb-24 px-4">
    <div className="max-w-5xl mx-auto bg-white border border-[#f4e7da] shadow-[0_25px_65px_rgba(16,24,40,0.08)] rounded-[22px] overflow-hidden">
      <div className="px-6 md:px-12 py-12 md:py-16 space-y-10 text-[#504f4c] leading-relaxed">

        <div className="space-y-6">
          <p className="text-base">
            Testa Care, a trading name of Sigma Universal Ltd ("we", "us", or "our"), uses cookies on our website (the "Service"). By using our Service, you consent to the use of cookies in accordance with this Cookies Policy.
          </p>
          <p>
            This Cookies Policy explains what cookies are, how we use them, how third parties we may work with use cookies, your choices regarding cookies, and where you can find further information.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            What are cookies?
          </h2>
          <p>
            Cookies are small text files that are placed on your computer or mobile device by websites that you visit. Cookies are widely used to make websites work efficiently, as well as to provide information to the owners of the site.
          </p>
          <p>
            Cookies allow a website to recognise your device and remember information about your visit, such as your preferences or previous actions.
          </p>
          <p>
            Cookies can be either:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Session cookies, which are deleted when you close your browser; or</li>
            <li>Persistent cookies, which remain on your device for a set period or until you delete them.</li>
          </ul>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            How Testa Care uses cookies
          </h2>
          <p>
            When you use and access our Service, we may place a number of cookie files in your web browser.
          </p>
          <p>
            We use cookies for the following purposes:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>to ensure the website functions correctly</li>
            <li>to improve the performance and usability of the Service</li>
            <li>to understand how visitors use our website through analytics</li>
            <li>to remember your preferences and settings</li>
          </ul>
          <p>
            We use both session and persistent cookies and different categories of cookies to operate the Service.
          </p>
        </div>

        <div className="space-y-6">
          <h3 className="text-2xl font-light text-[#2f4c74]">Essential cookies</h3>
          <p>
            Essential cookies are required for the basic operation of our website. These cookies enable core functionality such as security, accessibility, and page navigation. The website cannot function properly without these cookies.
          </p>
        </div>

        <div className="space-y-6">
          <h3 className="text-2xl font-light text-[#2f4c74]">Third-party cookies</h3>
          <p>
            In addition to our own cookies, we may also use cookies provided by trusted third-party services. These cookies may be used to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>collect anonymous analytics and usage statistics</li>
            <li>help us improve website performance and content</li>
          </ul>
          <p>
            Third-party providers are responsible for the cookies they set and their own privacy practices.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            What are your choices regarding cookies?
          </h2>
          <p>
            You can choose whether to accept or refuse cookies. Most web browsers automatically accept cookies, but you can usually modify your browser settings to decline cookies if you prefer.
          </p>
          <p>
            If you wish to delete cookies or instruct your web browser to delete or refuse cookies, please refer to the help pages of your browser.
          </p>
          <p>
            Please note that if you disable or refuse cookies:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>some parts of the website may not function properly</li>
            <li>your preferences may not be saved</li>
            <li>certain features or pages may not display correctly</li>
          </ul>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            Further information
          </h2>
          <p>
            For more information about cookies, including how to see what cookies have been set and how to manage or delete them, please visit your browser provider's help pages or reputable online resources about cookies.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            Changes to this Cookies Policy
          </h2>
          <p>
            We may update this Cookies Policy from time to time. Any changes will be posted on this page and will take effect immediately upon publication.
          </p>
        </div>

        <div className="space-y-6">
          <h2
            className="text-3xl md:text-4xl font-light text-[#0d0d0d]"
            style={{ fontFamily: "'Playfair Display', 'Times New Roman', serif" }}
          >
            Contact us
          </h2>
          <p>
            If you have any questions about this Cookies Policy or our use of cookies, please contact:
          </p>
          <div className="bg-[#fdf5ed] p-6 rounded-lg border border-[#f4e7da]">
            <p className="font-semibold text-[#0d0d0d]">Testa Care</p>
            <p>Sigma Universal Ltd</p>
            <p>Davyfield Road</p>
            <p>Blackburn</p>
            <p>Lancashire</p>
            <p>England</p>
            <p>BB1 2QY</p>
            <p className="mt-2">Email: <a href="mailto:info@testacare.co.uk" className="text-[#2f4c74] underline hover:text-[#244061]">info@testacare.co.uk</a></p>
            <p>Telephone: +44 (0)7767 054238</p>
          </div>
        </div>

        <p className="pt-6 border-t border-[#f1e3d6] text-sm tracking-[0.2em] uppercase text-[#2f4c74]">
          Updated: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}
        </p>
      </div>
    </div>
  </section>
);

export default CookieNoticeSection;
